// Propojení Slacku pro status 🌴 během dovolené (21.9.2026, schválený mockup).
//
// SlackPruh    pruh pod horní lištou na každé stránce, dokud člověk Slack
//              nepropojí (nebo když propojení přestalo platit). Nejde zavřít,
//              propojit ho má každý.
// SlackKarta   karta na stránce Můj účet: propojit, nebo stav Propojeno
//              s možností odpojit. V menu je jen řádek Můj účet (28.9.2026, #121).
import React, { useEffect, useState } from "react";
import { Palmtree } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSlackStatus, propojitSlack, odpojitSlack, nactiSlackStatus, ulozSlackSecret } from "../lib/slackStatus.js";
import { getRealUser } from "../lib/auth.js";
import { toast } from "./Toast.jsx";
import Card from "./Card.jsx";
import { confirmDialog } from "./confirmHost.jsx";

async function spustPropojeni(setBezi) {
  setBezi(true);
  try { await propojitSlack(); } catch (e) { toast(e.message || "Propojení se nepodařilo spustit", "error"); setBezi(false); }
}

export function SlackPruh() {
  const s = useSlackStatus();
  const [bezi, setBezi] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Návrat ze Slacku: ?slack=ok / zruseno / chyba → hláška a pryč z adresy.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const v = q.get("slack");
    if (!v) return;
    if (v === "ok") toast("Slack propojen. Během dovolené ti tam naskočí 🌴 samo.", "success");
    else if (v === "zruseno") toast("Propojení Slacku jsi zrušil(a), pruh zůstane, dokud ho nepropojíš.", "info");
    else toast("Propojení Slacku se nepovedlo, zkus to prosím znovu.", "error");
    q.delete("slack");
    const hledani = q.toString();
    navigate(location.pathname + (hledani ? `?${hledani}` : ""), { replace: true });
    nactiSlackStatus(true);
  }, [location.search, location.pathname, navigate]);

  if (!s || s.chyba || (s.propojeno && s.platne)) return null;
  if (!s.pripraveno) return getRealUser()?.role === "owner" ? <SlackSecretPruh /> : null;
  const znovu = s.propojeno && !s.platne;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-sky-200 bg-sky-50 px-4 py-2 lg:px-6" data-slack-pruh>
      <Palmtree className="h-4 w-4 shrink-0 text-sky-700" aria-hidden="true" />
      <span className="min-w-0 flex-1 text-sm text-sky-900">
        {znovu
          ? "Propojení Slacku přestalo platit, propoj ho prosím znovu, ať ti při dovolené naskočí status."
          : "Propoj si Slack, ať ti při dovolené naskočí status sám. Stačí jednou."}
      </span>
      <button
        type="button"
        disabled={bezi}
        onClick={() => spustPropojeni(setBezi)}
        className="btn btn-primary btn-sm"
      >
        {bezi ? "Otevírám Slack…" : "Propojit Slack"}
      </button>
    </div>
  );
}

export function SlackKarta({ jmeno = "" }) {
  const s = useSlackStatus();
  const [bezi, setBezi] = useState(false);
  if (!s || s.chyba || !s.pripraveno) return null;
  const ok = s.propojeno && s.platne;
  const odpoj = async () => {
    const ano = await confirmDialog({
      title: "Odpojit Slack?",
      description: "Během dovolené se ti pak status nastavovat nebude a v adminu se znovu objeví výzva k propojení.",
      confirmLabel: "Odpojit",
    });
    if (!ano) return;
    try { await odpojitSlack(); toast("Slack odpojen."); } catch (e) { toast(e.message, "error"); }
  };
  return (
    <Card title="Slack status při dovolené" ikona={Palmtree} data-slack-karta>
      <p className="text-sm text-slate-800">
        Během schválené dovolené se ti ve Slacku sám nastaví status s palmou a textem „Dovolená do …“.
        Poslední den dovolené zmizí. Propojení stačí jednou{jmeno ? `, platí jen pro tvůj účet ${jmeno}` : ""}.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {ok ? (
          <>
            <span className="rounded-lg bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">Propojeno</span>
            <button type="button" onClick={odpoj} className="btn btn-secondary btn-sm">Odpojit</button>
          </>
        ) : (
          <button type="button" disabled={bezi} onClick={() => spustPropojeni(setBezi)} className="btn btn-primary btn-sm">
            {bezi ? "Otevírám Slack…" : "Propojit Slack"}
          </button>
        )}
      </div>
    </Card>
  );
}

/**
 * Jen owner, jen jednou: dokud není uložený Client Secret Slack aplikace,
 * propojení nejde dokončit a ostatní pruh nevidí. V Netlify env už není
 * místo, proto se ukládá sem (šifrovaně do databáze).
 */
function SlackSecretPruh() {
  const [hodnota, setHodnota] = useState("");
  const [bezi, setBezi] = useState(false);
  const uloz = async (e) => {
    e.preventDefault();
    setBezi(true);
    try { await ulozSlackSecret(hodnota); toast("Uloženo. Výzva k propojení Slacku se teď ukáže všem.", "success"); }
    catch (err) { toast(err.message, "error"); setBezi(false); }
  };
  return (
    <form onSubmit={uloz} className="flex flex-wrap items-center gap-3 border-b border-sky-200 bg-sky-50 px-4 py-3 lg:px-6">
      <span className="text-2xl leading-none" aria-hidden="true">🌴</span>
      <div className="min-w-[190px] flex-1">
        <div className="text-[15px] font-bold text-sky-950">Slack status při dovolené: vlož Client Secret (jen jednou, vidíš jen ty)</div>
        <div className="text-[13px] text-sky-800">api.slack.com › Joky The Manager › Basic Information › App Credentials › Client Secret › Show</div>
      </div>
      <input
        type="password"
        autoComplete="off"
        value={hodnota}
        onChange={(e) => setHodnota(e.target.value)}
        placeholder="Client Secret"
        className="field min-h-[44px] w-full sm:w-72"
      />
      <button type="submit" disabled={bezi || !hodnota.trim()}
        className="min-h-[44px] rounded-xl bg-brand px-5 text-sm font-bold text-white shadow-sm transition active:scale-95 disabled:opacity-60 sm:hover:bg-brand-deep">
        {bezi ? "Ukládám…" : "Uložit"}
      </button>
    </form>
  );
}
